<template>
  <v-card class="owner-information qtm-border" variant="flat">
    <v-card-title>
      <div class="d-flex align-center justify-space-between project-title">
        <div>
          Project
        </div>
        <change-project-btn
          v-if="order.owner && order.state.can_edit_project"
          :project="project"
          :user="order.owner"
          @change="updateProject"
        />
      </div>
    </v-card-title>
    <v-card-text v-if="project">
      <div>
        <nuxt-link target="_blank" :to="{ name: 'projects-id', params: { id: project.id } }">
          {{ project.project_number }}
        </nuxt-link>
      </div>
      <div v-text="project.name" />
      <div v-text="deliveryAddress" />
    </v-card-text>
    <v-card-title>
      <div class="d-flex align-center justify-space-between">
        <div>
          Customer
        </div>
        <change-owner-btn :owner="order.owner" :project="project" @update:owner="updateOwner" />
      </div>
    </v-card-title>
    <v-card-text>
      <div>
        <nuxt-link target="_blank" :to="{ name: 'users-id-edit', params: { id: order.owner?.id } }">
          {{ order.owner?.first_name }} {{ order.owner?.last_name }}
        </nuxt-link>
      </div>
      <div v-text="order.owner?.email" />
      <div v-if="order.owner?.mobile_phone">
        M: {{ order.owner?.mobile_phone }}
      </div>
      <div v-if="order.owner?.phone">
        O: {{ order.owner?.phone }}
      </div>
    </v-card-text>
    <slot />
  </v-card>
</template>

<script setup lang="ts">
import ChangeOwnerBtn from '@/components/users/change-owner-btn.vue'
import ChangeProjectBtn from '@/components/jobsites/change-project-btn.vue'
import type { Jobsite, Order, User } from '@quotetome/materials-api'

export interface Props {
  order: Order
}
const props = defineProps<Props>()

const adminStore = useAdminStore()

const loading = ref(false)

const deliveryAddress = computed(() => {
  const keys = ['address', 'city', 'province']
  const prefix = props.order.hasPurchaseOrder ? 'shipto_' : ''
  const subject = props.order.hasPurchaseOrder ? props.order.pos[0] : props.order

  return keys.map(key => subject[`${prefix}${key}`]).join(', ')
})
const project = computed(() => props.order.jobsite)

const { $api, $error, $toast } = useNuxtApp()
const updateProject = async (project: Jobsite) => {
  const data = { jobsite: project.id, location: null }

  loading.value = true
  let order
  try {
    if (props.order.hasPurchaseOrder) {
      await updatePOProject(data)
      order = await $api.v1.rfqs.get(props.order.id!)
    }
    else {
      order = await updateOrderProject(data)
    }

    if (order) {
      adminStore.updateOrder(order)
      $toast.success('Project updated')
    }
    else {
      $toast.error('No order...')
    }
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const updateOrderProject = (payload) => {
  payload.id = props.order.id
  payload.freight_cost_code = ''
  payload.skus = props.order.skus.map(item => ({ ...item, cost_code: '' }))

  return $api.v1.rfqs.update(payload)
}

const updateOwner = async (user: User) => {
  loading.value = true
  try {
    const order = await $api.v1.rfqs.update({ id: props.order.id, owner: user.id })

    adminStore.updateOrder(order)
    $toast.success('Owner updated')
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const updatePOProject = (payload) => {
  const po = props.order.pos[0]

  payload.latest_change_order = {
    freight_cost_code: '',
    poskus: po!.poskus.map(item => ({ ...item, cost_code: '' })),
  }

  return $api.v1.purchaseOrders.update(po!.id!, payload)
}
</script>

<style lang="scss" scoped>
.project-title {
  width: 100%;
}
</style>
